import { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase/client';
import { Login } from './Login';
import { Register } from './Register';
import { User } from '@supabase/supabase-js';

interface AuthWrapperProps {
  children: React.ReactNode;
  onAuthSuccess?: () => void;
  requireAuth?: boolean; // Se true, exige login. Se false, permite acesso sem login
  onLoginRequest?: () => void; // Callback quando precisa de login
}

export function AuthWrapper({ children, onAuthSuccess, requireAuth = true, onLoginRequest }: AuthWrapperProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);

      // If user is logged in, check their groups
      if (session?.user) {
        checkUserGroups(session.user);
      }
    });

    // Listen for changes on auth state (logged in, signed out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);

      // If user just logged in, check their groups
      if (session?.user) {
        checkUserGroups(session.user);
      }
    });

    return () => subscription.unsubscribe();
  }, [onAuthSuccess]);

  const checkUserGroups = async (currentUser: User) => {
    try {
      console.log('Verificando grupos do usuário:', currentUser.email);
      const emailNormalized = (currentUser.email || '').trim().toLowerCase();

      // Buscar participações do usuário
      const { data: participations, error: participationError } = await supabase
        .from('participants')
        .select('id, group_id, name')
        .ilike('email', emailNormalized);

      console.log('Participações encontradas:', participations);
      console.log('Erro na consulta:', participationError);

      if (participationError) {
        console.error('Erro ao buscar participações:', participationError);
        return;
      }

      if (!participations || participations.length === 0) {
        console.log('Usuário não está em nenhum grupo');
        // Usuário não está em nenhum grupo
        return;
      }

      if (participations.length === 1) {
        console.log('Usuário está em apenas 1 grupo, redirecionando...');
        // Usuário está em apenas 1 grupo - redirecionar direto
        const groupId = participations[0].group_id;
        const participantId = participations[0].id;

        // Salvar no localStorage
        localStorage.setItem('amigoSecretoGroupId', groupId);
        localStorage.setItem('amigoSecretoParticipantId', participantId);

        // Disparar evento de sucesso
        onAuthSuccess?.();
      } else {
        console.log(`Usuário está em ${participations.length} grupos, indo para Home...`);
        // Usuário está em múltiplos grupos - vai para Home onde tem o carrossel
        // Não faz nada aqui, deixa o App.tsx verificar e mostrar o carrossel
      }
    } catch (error) {
      console.error('Erro ao verificar grupos do usuário:', error);
    }
  };

  const handleAuthSuccess = () => {
    onAuthSuccess?.();
  };

  const toggleAuthMode = () => {
    setAuthMode(authMode === 'login' ? 'register' : 'login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-green-50">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-red-500 border-t-transparent mx-auto mb-4" />
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // Se não requer autenticação, renderiza o children mesmo sem usuário
  if (!requireAuth) {
    return <>{children}</>;
  }

  // Se requer autenticação e não tem usuário, mostra tela de login
  if (!user) {
    return authMode === 'login' ? (
      <Login
        onLoginSuccess={handleAuthSuccess}
        onToggleMode={toggleAuthMode}
      />
    ) : (
      <Register
        onRegisterSuccess={handleAuthSuccess}
        onToggleMode={toggleAuthMode}
      />
    );
  }

  return <>{children}</>;
}